import React, { FC, useState, FormEvent, ChangeEvent } from "react";
import { useSearchParams } from "react-router-dom";

import { toast } from "react-toastify";

import { formatDateTime } from "~/libs";

import {
  useBoolean,
  useEditorState,
  useRightSidebarState,
  useApiPolling,
  useEditorStateLinkContentGetter,
} from "~/hooks";

import {
  EmployeeLayout,
  EmployeeLayoutMainContent,
  EmployeeLayoutRightSidebar,
} from "~/components/layouts";

import { useSearchMembersInsightPosts } from "./hooks/useSearchMembersInsightPosts";
import {
  InsightPostDetailWithEditForm,
  InsightPostForm,
  InsightPostSearchForm,
  InsightPostsCountCalendar,
  InsightPostsPerformance,
  TodaysInsightPost,
  InsightPostCommentsWithInsightPost,
} from "../../../components";
import {
  useApisInsightPostsCreate,
  useApisInsightPostsNew,
  useApisInsightPostsInsightPostCommentsIndex,
} from "../../../hooks/members";
import { InsightPostTypes, AllInsightPostTypes } from "../../../types";

import {
  OptionType,
  PreviewWithFileType,
  InsightPostTypeType,
} from "~/domains";

import {
  DatePicker,
  RightSidebarToggleIconWithLabel,
  InfiniteScroll,
  FilterIconWithSortMenu,
} from "~/ui";
import { TabBlocks } from "~/ui";

export const MembersInsightPostsIndex: FC = () => {
  const [searchParams] = useSearchParams();

  // URLパラメータからinsightPostTypeを導出
  const typeParam = searchParams.get("type");
  const insightPostType: InsightPostTypeType =
    AllInsightPostTypes.find((type) => type.type === typeParam) ??
    InsightPostTypes.MEMO;
  const {
    insightPosts,
    hasNextPage,
    isLoading,
    selectableDropdownHasBookmarkIds,
    selectedDropdownHasBookmarkId,
    selectableDropdownHasCommentIds,
    selectedDropdownHasCommentId,
    selectableDropdownSharedIds,
    selectedDropdownSharedId,
    startDate,
    endDate,
    sort,
    selectableJobCategories,
    selectedJobCategories,
    optionSelectableJobCategoryPhases,
    optionSelectedJobCategoryPhases,
    keyword,
    addInsightPost,
    deleteIndexInsightPost,
    updateIndexInsightPost,
    handleDateRangeWithSearch,
    onChangeJobCategoryPhases,
    onChangeJobCategories,
    onChangeDateRange,
    setSort,
    fetchNextPage,
    onChangeKeyword,
    onSearchSubmit,
    onChangeDropdownHasBookmarkId,
    onChangeDropdownHasCommentId,
    onChangeDropdownSharedId,
    onConditionReset,
  } = useSearchMembersInsightPosts();
  const [files, setFiles] = useState<PreviewWithFileType[]>([]);
  const [postDate, setPostDate] = useState<Date>(new Date());
  const [jobCategoryPhase, setJobCategoryPhase] = useState<OptionType>({
    label: "すべて",
    value: "",
  });

  const {
    isChecked: shareable,
    setTrue: setShareableTrue,
    onChange: onChangeShareable,
  } = useBoolean(true);
  const [editorState] = useEditorState();
  const { fetchedData } = useEditorStateLinkContentGetter({
    watchEditorState: editorState.value,
    isWatch: true,
  });

  const {
    isSearchOpen,
    isReplyOpen,
    setReplyOpen,
    searchCalendarOpenToggle,
    setCalendarOpen,
    isOpen,
    isCalendarOpen,
    setClose,
    currentOpenLabel,
  } = useRightSidebarState("isCalendarOpen");
  const { mutate: postInsightPosts, isLoading: isSubmitting } =
    useApisInsightPostsCreate();
  const { data: fetchNewData } = useApisInsightPostsNew();

  const [selectGetCommentInsightPostId, setSelectGetCommentInsightPostId] =
    useState<string>("");

  const { data: insightPostCommentsIndexData, refetch: commentsRefetch } =
    useApisInsightPostsInsightPostCommentsIndex({
      insightPostId: selectGetCommentInsightPostId,
      config: {
        enabled: Boolean(selectGetCommentInsightPostId),
      },
    });

  const handleSearchDateOnChange = (date: Date) => {
    handleDateRangeWithSearch(date, date);
    setPostDate(date);
  };

  useApiPolling({
    enabled: isReplyOpen && Boolean(selectGetCommentInsightPostId),
    interval: 1000,
    pollingFunction: commentsRefetch,
    timeoutDuration: null,
  });

  const handleChangeJobCategoryPhase = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    setJobCategoryPhase({ label: e.target.name, value: e.target.value });
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const body = {
      content: editorState.jsonContent,
      contentText: editorState.plainText,
      postDate: formatDateTime(postDate, "yyyy-MM-dd"),
      shareable: shareable,
      jobCategoryPhaseId: jobCategoryPhase?.value,
      attachFiles: files,
      quotePostId: fetchedData?.id,
      postTypeId: insightPostType.id,
    };

    postInsightPosts(
      {
        body,
      },
      {
        onSuccess: (data) => {
          toast(data?.message);
          reset();
          addInsightPost(data.insightPost);
          openComment(data.insightPost.id);
        },
      },
    );
  };

  const reset = () => {
    editorState.setPlainText("");
    setFiles([]);
    setShareableTrue();
    setJobCategoryPhase({ label: "すべて", value: "" });
  };

  const openComment = (insightPostId: string) => {
    setSelectGetCommentInsightPostId(insightPostId);
    setReplyOpen();
  };

  const breadCrumbItems = [{ label: "メモログ", href: "/insight_posts" }];

  return (
    <>
      <EmployeeLayout withRightSidebar breadCrumbItems={breadCrumbItems}>
        <EmployeeLayoutMainContent withRightSidebar>
          {!isSearchOpen && <TodaysInsightPost type={insightPostType.type} />}
          {!isSearchOpen && (
            <DatePicker
              selectDate={postDate}
              onChange={handleSearchDateOnChange}
              parentClassName="mt-6"
            />
          )}
          {!isSearchOpen && fetchNewData && (
            <div className="mt-6">
              <TabBlocks
                tabs={[
                  InsightPostTypes.MEMO,
                  InsightPostTypes.INTERNAL_QUESTION,
                  InsightPostTypes.CHALLENGE,
                ].map((tab) => ({
                  id: tab.id,
                  name: tab.name,
                  isActive: insightPostType.id === tab.id,
                  href: `/insight_posts?type=${tab.type}`,
                }))}
              />
              <InsightPostForm
                isSubmitting={isSubmitting}
                mentions={fetchNewData.mentionableEmployees}
                files={files}
                onChangeFiles={setFiles}
                editorState={editorState.value}
                setEditorState={editorState.onChange}
                selectableJobCategoryPhases={
                  fetchNewData.selectableJobCategoryPhases
                }
                selectedJobCategoryPhase={jobCategoryPhase}
                onChangeJobCategoryPhase={handleChangeJobCategoryPhase}
                shareable={shareable}
                setShareable={onChangeShareable}
                handleSubmit={handleSubmit}
                defaultAutoFucus
                blockQuote={fetchedData}
              />
            </div>
          )}
          <FilterIconWithSortMenu
            likeSort={() => setSort("likeCountDesc")}
            bookmarkSort={() => setSort("bookmarkCountDesc")}
            createdAtAscSort={() => setSort("createdAtAsc")}
            createdAtDescSort={() => setSort("createdAtDesc")}
            currentSortType={sort}
            className={isSearchOpen ? "mt-6" : "mt-12"}
          />
          <InfiniteScroll
            itemsLength={insightPosts.length}
            nextFetchFunction={fetchNextPage}
            hasMore={hasNextPage}
            isLoading={isLoading}
            className="border-t-2 border-secondary-200"
            noContentMessage="メモログがありません"
          >
            {insightPosts.map((post, index) => (
              <InsightPostDetailWithEditForm
                key={post.id}
                insightPost={post}
                mentions={fetchNewData?.mentionableEmployees || []}
                selectableJobCategoryPhases={
                  fetchNewData?.selectableJobCategoryPhases || []
                }
                openComment={openComment}
                deleteItem={() => {
                  deleteIndexInsightPost(index);
                  if (selectGetCommentInsightPostId === post.id) {
                    setSelectGetCommentInsightPostId("");
                    setClose();
                  }
                }}
                updateItem={(newItem) => updateIndexInsightPost(newItem, index)}
              />
            ))}
          </InfiniteScroll>
        </EmployeeLayoutMainContent>
        <EmployeeLayoutRightSidebar>
          <>
            <RightSidebarToggleIconWithLabel
              iconOnClick={searchCalendarOpenToggle}
              handleOnClose={isCalendarOpen ? undefined : setCalendarOpen}
              label={currentOpenLabel}
              isOpen={isOpen}
            >
              {isCalendarOpen && (
                <div className="mt-6 space-y-6">
                  <InsightPostsCountCalendar />
                  <InsightPostsPerformance />
                </div>
              )}
              {isSearchOpen && (
                <InsightPostSearchForm
                  onSubmit={onSearchSubmit}
                  onConditionReset={onConditionReset}
                  date={{
                    startDate: startDate,
                    endDate: endDate,
                    onChange: onChangeDateRange,
                  }}
                  jobCategoryAndJobCategoryPhase={{
                    selectableJobCategories: selectableJobCategories,
                    selectedJobCategories: selectedJobCategories,
                    selectableJobCategoryPhases:
                      optionSelectableJobCategoryPhases,
                    selectedJobCategoryPhases: optionSelectedJobCategoryPhases,
                    onJobCategoryChange: onChangeJobCategories,
                    onJobCategoryPhaseChange: onChangeJobCategoryPhases,
                  }}
                  hasComment={{
                    options: selectableDropdownHasCommentIds,
                    value: selectedDropdownHasCommentId,
                    onChange: onChangeDropdownHasCommentId,
                  }}
                  hasBookmark={{
                    options: selectableDropdownHasBookmarkIds,
                    value: selectedDropdownHasBookmarkId,
                    onChange: onChangeDropdownHasBookmarkId,
                  }}
                  shared={{
                    options: selectableDropdownSharedIds,
                    value: selectedDropdownSharedId,
                    onChange: onChangeDropdownSharedId,
                  }}
                  searchKeyword={{
                    value: keyword,
                    onChange: onChangeKeyword,
                  }}
                />
              )}
              {isReplyOpen && insightPostCommentsIndexData && (
                <InsightPostCommentsWithInsightPost
                  className="mt-6"
                  insightPost={insightPostCommentsIndexData.insightPost}
                  mentions={fetchNewData?.mentionableEmployees || []}
                  insightPostComments={
                    insightPostCommentsIndexData.insightPostComments || []
                  }
                  refetch={commentsRefetch}
                />
              )}
            </RightSidebarToggleIconWithLabel>
          </>
        </EmployeeLayoutRightSidebar>
      </EmployeeLayout>
    </>
  );
};
